import {useCallback, useEffect, useState} from 'react';
import {useIVStore, DEFAULT_IMAGE_DETAILS, IVStoreState} from '@chekin/shared';
import {STORAGE_KEYS} from 'utils/constants';
import {
  getFeatureFlags,
  IV_DOCUMENT_IMAGE_TYPES,
  IVIntent,
  SessionStorage,
} from '@chekin/core';
import {useIsRemoteIV} from 'hooks/iv/useRemoteStartVerificationLink';
import useBuildOCRDetectedData from 'hooks/useBuildOCRDetectedData';
import {API} from '@chekin/api';

export const setIVIntentRemoteSession = (intentId: string) => {
  SessionStorage.set(STORAGE_KEYS.ivIntentRemoteSession, intentId);
};

export const getIVIntentRemoteSession = (): string | null =>
  getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')
    ? SessionStorage.get(STORAGE_KEYS.ivIntentRemoteSession)
    : null;

export const removeIVIntentRemoteSession = () =>
  SessionStorage.remove(STORAGE_KEYS.ivIntentRemoteSession);

export const convertImagesDetailsToStoreState = (
  imagesDetails?: IVIntent['image_details'],
) => {
  if (!imagesDetails) {
    return {imagesDetails: DEFAULT_IMAGE_DETAILS};
  }

  Object.values(IV_DOCUMENT_IMAGE_TYPES).reduce((acc, key) => {
    const imageDetails = imagesDetails[key] || DEFAULT_IMAGE_DETAILS[key];

    acc[key] = {
      ...imageDetails,
      uploaded: Boolean(imageDetails.image),
    };
    return acc;
  }, {} as Required<IVIntent>['image_details']);

  return {
    imagesDetails: {
      ...DEFAULT_IMAGE_DETAILS,
      ...imagesDetails,
    },
  };
};

const convertIvIntentToStoreState = (ivIntentItem: IVIntent): Partial<IVStoreState> => {
  return {
    setupData: {
      nationality: ivIntentItem.nationality,
      documentType: ivIntentItem.type_doc,
    },
    ocrResultData: {
      ocrPassed: Boolean(ivIntentItem.document_passed),
    },
    isIvFailed: ivIntentItem.is_iv_failed,
    isPropertyQRScanned: ivIntentItem.is_property_qr_scanned,
    livenessResultData: ivIntentItem.liveness_data,
    biomatchResultData: {
      biomatchPassed: Boolean(ivIntentItem.biomatch_passed),
      distance: null,
    },
    ...convertImagesDetailsToStoreState(ivIntentItem.image_details),
  };
};

export const useIVRemoteSync = () => {
  const {buildFormData} = useBuildOCRDetectedData();
  const {intentId: urlIntentId} = useIsRemoteIV();
  const {sync, setRemoteSession} = useIVStore();
  const [isLoaded, setIsLoaded] = useState(false);

  const loadRemoteState = useCallback(
    async ({postHandler}: {postHandler?: () => void}) => {
      const intentId = getIVIntentRemoteSession();
      if (intentId) {
        const data = await API.identityVerification.getIvIntent(intentId);
        sync(convertIvIntentToStoreState(data));

        if (data?.mrz) {
          const builtFormData = buildFormData(data.mrz);
          SessionStorage.set(STORAGE_KEYS.personalFormData, builtFormData);
        }

        postHandler?.();
      }
    },
    [buildFormData, sync],
  );

  useEffect(() => {
    if (urlIntentId && getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')) {
      setRemoteSession(true);
      setIVIntentRemoteSession(urlIntentId);
      API.identityVerification.startIVOnMobile(urlIntentId);
    }
  }, [urlIntentId, setRemoteSession]);

  useEffect(() => {
    const asyncIVIntentRemoteSession = async () => {
      const intentId = getIVIntentRemoteSession();
      if (intentId) {
        const data = await API.identityVerification.getIvIntent(intentId || '');
        sync(convertIvIntentToStoreState(data));
      }
      setIsLoaded(true);
    };

    if (getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')) {
      void asyncIVIntentRemoteSession();
    }
  }, [sync]);

  return {
    isLoaded: getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE') ? isLoaded : true,
    loadRemoteState,
  };
};
